<template>
  <div>
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Posters de beneficios</h1>
      <v-btn rounded color="primary" depressed @click="openCreate">
        <v-icon left>mdi-plus</v-icon>
        <span class="d-none d-md-inline">Agregar poster</span>
      </v-btn>
    </v-row>

    <v-row class="ma-0 mb-3" align="center">
      <v-col cols="12" sm="4" md="3" class="pa-0 pr-sm-3 mb-2 mb-sm-0">
        <v-select
          v-model="filters.visible"
          :items="booleanFilterItems"
          label="Visible"
          outlined dense hide-details
        />
      </v-col>
      <v-col cols="12" sm="4" md="3" class="pa-0 pr-sm-3 mb-2 mb-sm-0">
        <v-select
          v-model="filters.available"
          :items="booleanFilterItems"
          label="Disponible"
          outlined dense hide-details
        />
      </v-col>
      <v-col cols="12" sm="4" md="3" class="pa-0">
        <v-select
          v-model="filters.countryId"
          :items="countries"
          item-text="text"
          item-value="id"
          label="País"
          :loading="loadingCountries"
          outlined dense hide-details clearable
        />
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="posterAds"
      :loading="loadingData"
      :options.sync="options"
      :server-items-length="totalItems"
      class="elevation-1"
      :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
      no-data-text="No hay posters"
    >
      <template v-slot:item.visible="{ item }">
        <v-icon :color="item.visible ? 'success' : ''">{{ item.visible ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
      </template>
      <template v-slot:item.available="{ item }">
        <v-icon :color="item.available ? 'success' : ''">
          {{ item.available ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}
        </v-icon>
      </template>
      <template v-slot:item.isb2b="{ item }">
        {{ item.isb2b ? 'Sí' : 'No' }}
      </template>
      <template v-slot:item.countryId="{ item }">
        {{ countryName(item.countryId) }}
      </template>
      <template v-slot:item.dates="{ item }">
        {{ dateRange(item) }}
      </template>
      <template v-slot:item.scope="{ item }">
        <v-tooltip v-if="contractNames(item).length" top>
          <template v-slot:activator="{ on, attrs }">
            <v-chip small v-bind="attrs" v-on="on">{{ scopeLabel(item) }}</v-chip>
          </template>
          <div v-for="name in contractNames(item)" :key="name">{{ name }}</div>
        </v-tooltip>
        <v-chip v-else small outlined>{{ scopeLabel(item) }}</v-chip>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn icon small @click="openEdit(item)">
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn icon small color="error" @click="openDelete(item)">
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <poster-ad-modal
      v-model="showFormDialog"
      :poster-ad="editingPosterAd"
      :countries="countries"
      :alliances="alliances"
      :contract-index="contractIndex"
      @saved="onSaved"
    />

    <v-dialog v-model="deleteDialog" max-width="480" :persistent="deleting">
      <v-card class="pa-2">
        <v-card-title>Eliminar poster</v-card-title>
        <v-card-text>
          ¿Estás seguro de que deseas eliminar
          <strong>{{ selectedPosterAd ? selectedPosterAd.title : '' }}</strong>?
          Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded :disabled="deleting" @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" rounded depressed :loading="deleting" @click="confirmDelete">Sí, eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import PosterAdModal from '../components/PosterAdModal.vue';
import PosterAdsService from '../services/poster-ads.service';
import CountryService from '../services/countries.service';
import AlliancesService from '../services/alliances.service';
import ContratoService from '../services/contrato.service';
import { utilsMixin } from '../mixins/utilsMixin';
import { scopeLabel, toContractOption } from '../utils/poster-ads.utils';

const formatDate = (value) => (value ? String(value).substring(0, 10) : '');

export default {
  name: 'PosterAds',
  components: { PosterAdModal },
  mixins: [utilsMixin],
  data() {
    return {
      posterAds: [],
      totalItems: 0,
      loadingData: false,
      options: { page: 1, itemsPerPage: 10 },
      filters: { visible: null, available: null, countryId: null },
      booleanFilterItems: [
        { text: 'Todos', value: null },
        { text: 'Sí', value: true },
        { text: 'No', value: false }
      ],
      headers: [
        { text: 'Título', value: 'title', sortable: false },
        { text: 'Orden', value: 'order', sortable: false, align: 'center' },
        { text: 'Visible', value: 'visible', sortable: false, align: 'center' },
        { text: 'Disponible', value: 'available', sortable: false, align: 'center' },
        { text: 'B2B', value: 'isb2b', sortable: false, align: 'center' },
        { text: 'País', value: 'countryId', sortable: false },
        { text: 'Vigencia', value: 'dates', sortable: false },
        { text: 'Alcance', value: 'scope', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ],
      countries: [],
      loadingCountries: false,
      alliances: [],
      contractIndex: {},
      showFormDialog: false,
      editingPosterAd: null,
      deleteDialog: false,
      deleting: false,
      selectedPosterAd: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },
  watch: {
    // Only page changes reload: the table also emits options on mount with the same values.
    'options.page'() {
      this.loadPosterAds();
    },
    'options.itemsPerPage'() {
      this.loadPosterAds();
    },
    filters: {
      handler() {
        if (this.options.page === 1) this.loadPosterAds();
        else this.options = { ...this.options, page: 1 };
      },
      deep: true
    }
  },
  created() {
    this.loadPosterAds();
    this.loadCountries();
    this.loadAlliances();
    this.loadContractIndex();
  },
  methods: {
    scopeLabel,
    async loadPosterAds() {
      this.loadingData = true;
      const res = await PosterAdsService.getAll({
        page: this.options.page,
        limit: this.options.itemsPerPage,
        ...this.filters
      });
      if (res.status === 'ok') {
        this.posterAds = res.data.posterAds || [];
        this.totalItems = res.data.totalItems || 0;
      } else {
        this.showErrorSnackbar('Error al cargar los posters');
      }
      this.loadingData = false;
    },
    async loadCountries() {
      this.loadingCountries = true;
      try {
        const res = await CountryService.getAllCountries(true);
        if (res && res.status === 'ok') {
          this.countries = res.data.map((c) => ({ text: c.name, id: c.id }));
        }
      } catch {
        this.showErrorSnackbar('Error al cargar los países');
      }
      this.loadingCountries = false;
    },
    async loadAlliances() {
      const res = await AlliancesService.getAll(1, 200, null, null);
      if (res.status === 'ok') {
        this.alliances = res.data.alliances || [];
      } else {
        this.showErrorSnackbar('Error al cargar las alianzas');
      }
    },
    // One request for every contract, so the "Alcance" column and the edit form can show
    // contract and company names without a request per poster.
    async loadContractIndex() {
      const res = await ContratoService.getAll();
      if (res.status !== 'ok' || !Array.isArray(res.data)) {
        this.showErrorSnackbar('Error al cargar los contratos');
        return;
      }
      this.contractIndex = res.data.reduce((index, contract) => {
        index[contract.id] = toContractOption(contract);
        return index;
      }, {});
    },
    countryName(countryId) {
      if (!countryId) return 'Todos';
      const country = this.countries.find((c) => c.id === countryId);
      return country ? country.text : '—';
    },
    dateRange(posterAd) {
      const start = formatDate(posterAd.start_date);
      const end = formatDate(posterAd.end_date);
      if (!start && !end) return 'Sin fechas';
      return `${start || '…'} → ${end || 'sin término'}`;
    },
    contractNames(posterAd) {
      if (!Array.isArray(posterAd.contratoId)) return [];
      return posterAd.contratoId.map((id) => {
        const contract = this.contractIndex[id];
        return contract ? `${contract.companyName} · ${contract.label}` : id;
      });
    },
    openCreate() {
      this.editingPosterAd = null;
      this.showFormDialog = true;
    },
    openEdit(posterAd) {
      this.editingPosterAd = posterAd;
      this.showFormDialog = true;
    },
    onSaved(message) {
      this.showSuccessSnackbar(message);
      this.loadPosterAds();
    },
    openDelete(posterAd) {
      this.selectedPosterAd = posterAd;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      this.deleting = true;
      const res = await PosterAdsService.remove(this.selectedPosterAd.id);
      this.deleting = false;
      if (res.status === 'ok') {
        this.deleteDialog = false;
        this.selectedPosterAd = null;
        this.showSuccessSnackbar('Poster eliminado correctamente');
        this.loadPosterAds();
      } else {
        this.showErrorSnackbar('Error al eliminar el poster');
      }
    }
  }
};
</script>
